// The primary navigation for the editor-first shell (C0-R): a collapsible left
// rail listing the user's models (active + archived), the active one highlighted,
// "+ New" at the top (the freemium wall surfaces here via ModelsProvider), a
// per-model menu (rename inline / archive / delete / open in browse), and a Browse
// All entry. Fully keyboard-navigable (Tab + arrow keys), ARIA-labelled. Reads/
// writes only ModelsProvider + the route — no editor internals.
import { useRef, useState, type KeyboardEvent } from 'react';
import { Link, useMatch, useNavigate } from 'react-router-dom';
import type { Model } from '../api/types';
import { useModels } from '../models/ModelsContext';
import { modelThumbnail } from '../lib/thumbnail';
import { APP_NAME } from '../lib/appConfig';
import { cn } from '../lib/cn';
import UpgradeCta from './UpgradeCta';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ScrollArea } from './ui/scroll-area';
import { Skeleton } from './ui/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from './ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  ArchiveIcon,
  ArchiveRestoreIcon,
  ChevronDownIcon,
  GridIcon,
  MoreIcon,
  PanelLeftIcon,
  PencilIcon,
  PlusIcon,
  TrashIcon,
} from './icons';

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

// Arrow-key roving focus across the model links (a11y: keyboard model-switching).
function onListKeyDown(e: KeyboardEvent<HTMLElement>) {
  if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
  const items = Array.from(
    e.currentTarget.querySelectorAll<HTMLElement>('[data-model-link]'),
  );
  const idx = items.indexOf(document.activeElement as HTMLElement);
  if (idx === -1) return;
  e.preventDefault();
  const next = e.key === 'ArrowDown' ? idx + 1 : idx - 1;
  items[(next + items.length) % items.length]?.focus();
}

function Thumb({ model, size }: { model: Model; size: number }) {
  const t = modelThumbnail(model);
  return (
    <span
      className="flex shrink-0 items-center justify-center rounded-md text-[0.7rem] font-semibold text-white shadow-sm"
      style={{ background: t.gradient, width: size, height: size }}
      aria-hidden="true"
    >
      {t.initial}
    </span>
  );
}

function ModelItem({
  model,
  collapsed,
  activeId,
}: {
  model: Model;
  collapsed: boolean;
  activeId: string | undefined;
}) {
  const { rename, archive, unarchive, remove } = useModels();
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(model.name);
  const isActive = model.id === activeId;

  function commitRename() {
    const name = draft.trim();
    setEditing(false);
    if (name && name !== model.name) void rename(model.id, name);
    else setDraft(model.name);
  }

  const row = (
    <div
      className={cn(
        'group relative flex items-center gap-2 rounded-md px-2 py-1.5 transition-colors',
        isActive ? 'bg-accent' : 'hover:bg-accent/60',
        collapsed && 'justify-center px-0',
      )}
    >
      {isActive ? (
        <span className="absolute left-0 top-1/2 h-5 w-0.5 -translate-y-1/2 rounded-full bg-primary" />
      ) : null}
      {editing && !collapsed ? (
        <>
          <Thumb model={model} size={collapsed ? 32 : 24} />
          <Input
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commitRename}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commitRename();
              if (e.key === 'Escape') {
                setDraft(model.name);
                setEditing(false);
              }
            }}
            className="h-7 flex-1 px-1.5 py-0 text-sm"
            aria-label={`Rename ${model.name}`}
          />
        </>
      ) : (
        <>
          <Link
            to={`/editor/${model.id}`}
            data-model-link
            aria-current={isActive ? 'page' : undefined}
            className="flex min-w-0 flex-1 items-center gap-2 rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
            title={collapsed ? model.name : undefined}
          >
            <Thumb model={model} size={collapsed ? 32 : 24} />
            {!collapsed ? (
              <span
                className={cn(
                  'truncate text-sm',
                  isActive ? 'font-medium text-foreground' : 'text-muted-foreground',
                )}
              >
                {model.name}
              </span>
            ) : null}
          </Link>
          {!collapsed ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 shrink-0 opacity-0 transition-opacity focus-visible:opacity-100 group-hover:opacity-100 data-[state=open]:opacity-100"
                  aria-label={`Actions for ${model.name}`}
                >
                  <MoreIcon />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                <DropdownMenuItem onSelect={() => setEditing(true)}>
                  <PencilIcon />
                  Rename
                </DropdownMenuItem>
                {model.archived ? (
                  <DropdownMenuItem onSelect={() => void unarchive(model.id)}>
                    <ArchiveRestoreIcon />
                    Unarchive
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem onSelect={() => void archive(model.id)}>
                    <ArchiveIcon />
                    Archive
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onSelect={() => navigate('/browse')}>
                  <GridIcon />
                  Open in Browse
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  destructive
                  onSelect={() => void remove(model.id, { currentModelId: activeId })}
                >
                  <TrashIcon />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}
        </>
      )}
    </div>
  );

  if (collapsed) {
    return (
      <li>
        <Tooltip>
          <TooltipTrigger asChild>{row}</TooltipTrigger>
          <TooltipContent side="right">{model.name}</TooltipContent>
        </Tooltip>
      </li>
    );
  }
  return <li>{row}</li>;
}

export default function ModelSidebar({ collapsed, onToggle }: SidebarProps) {
  const { active, archived, loading, create } = useModels();
  const editorMatch = useMatch('/editor/:modelId');
  const activeId = editorMatch?.params.modelId;
  const [showArchived, setShowArchived] = useState(false);
  const creatingRef = useRef(false);

  async function onNew() {
    if (creatingRef.current) return;
    creatingRef.current = true;
    await create('Untitled');
    creatingRef.current = false;
  }

  return (
    <nav
      aria-label="Models"
      className={cn(
        'flex shrink-0 flex-col border-r border-border bg-card transition-[width] duration-200',
        collapsed ? 'w-[60px]' : 'w-[264px]',
      )}
    >
      {/* Wordmark + collapse toggle */}
      <div className={cn('flex h-12 items-center border-b border-border px-3', collapsed && 'justify-center px-0')}>
        {!collapsed ? (
          <Link to="/" className="flex items-center rounded-md bg-primary px-2 py-1" aria-label={APP_NAME}>
            {/* Plate uses the brand accent (primary = --cad-accent, the same purple as
                the New model button and the editor's bottom bar) so the transparent
                logo stays visible on any theme instead of vanishing into a dark panel. */}
            <img src="/logo.svg" alt={APP_NAME} className="h-8 w-auto" />
          </Link>
        ) : null}
        <Button
          variant="ghost"
          size="icon"
          className={cn('h-8 w-8', !collapsed && 'ml-auto')}
          onClick={onToggle}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-expanded={!collapsed}
        >
          <PanelLeftIcon />
        </Button>
      </div>

      {/* New */}
      <div className={cn('p-2', collapsed && 'flex justify-center')}>
        {collapsed ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button size="icon" className="h-9 w-9" onClick={() => void onNew()} aria-label="New model">
                <PlusIcon />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="right">New model</TooltipContent>
          </Tooltip>
        ) : (
          <Button className="w-full justify-start gap-2" onClick={() => void onNew()}>
            <PlusIcon />
            New model
          </Button>
        )}
      </div>

      <ScrollArea className="flex-1">
        <div className="px-2 pb-2" onKeyDown={onListKeyDown}>
          {loading ? (
            <ul className="space-y-1.5 px-0.5 py-1">
              {[0, 1, 2].map((i) => (
                <li key={i} className="flex items-center gap-2">
                  <Skeleton className="h-6 w-6 rounded-md" />
                  {!collapsed ? <Skeleton className="h-4 flex-1" /> : null}
                </li>
              ))}
            </ul>
          ) : (
            <>
              {!collapsed ? (
                <p className="px-2 py-1 text-[0.7rem] font-medium uppercase tracking-wide text-muted-foreground">
                  Active
                </p>
              ) : null}
              {active.length === 0 ? (
                !collapsed ? (
                  <p className="px-2 py-2 text-xs text-muted-foreground">
                    No models yet — “New model” starts one.
                  </p>
                ) : null
              ) : (
                <ul className="space-y-0.5" role="list">
                  {active.map((m) => (
                    <ModelItem key={m.id} model={m} collapsed={collapsed} activeId={activeId} />
                  ))}
                </ul>
              )}

              {archived.length > 0 && !collapsed ? (
                <div className="mt-2">
                  <button
                    className="flex w-full items-center gap-1 rounded-sm px-2 py-1 text-[0.7rem] font-medium uppercase tracking-wide text-muted-foreground outline-none hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
                    onClick={() => setShowArchived((s) => !s)}
                    aria-expanded={showArchived}
                  >
                    <ChevronDownIcon
                      className={cn('transition-transform', !showArchived && '-rotate-90')}
                      size="0.85em"
                    />
                    Archived ({archived.length})
                  </button>
                  {showArchived ? (
                    <ul className="mt-0.5 space-y-0.5" role="list">
                      {archived.map((m) => (
                        <ModelItem key={m.id} model={m} collapsed={collapsed} activeId={activeId} />
                      ))}
                    </ul>
                  ) : null}
                </div>
              ) : null}
            </>
          )}
        </div>
      </ScrollArea>

      {/* Upgrade upsell — bottom of the rail, free plan only (hidden for paid). */}
      <UpgradeCta collapsed={collapsed} />

      {/* Browse all */}
      <div className="border-t border-border p-2">
        {collapsed ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="icon" className="h-9 w-9" asChild>
                <Link to="/browse" aria-label="Browse all models">
                  <GridIcon />
                </Link>
              </Button>
            </TooltipTrigger>
            <TooltipContent side="right">Browse all models</TooltipContent>
          </Tooltip>
        ) : (
          <Button variant="ghost" className="w-full justify-start gap-2 text-muted-foreground" asChild>
            <Link to="/browse">
              <GridIcon />
              Browse all models
            </Link>
          </Button>
        )}
      </div>
    </nav>
  );
}
